import type { Attendee } from 'hb-react/shared/apps/Events';
import type {
  AddonItem,
  DonationItem,
  TicketItem,
} from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import type { Event } from 'hb-react/shared/apps/Events/types';
import type {
  Cart,
  TicketCartItem,
  AddonCartItem,
  DonationCartItem,
} from 'hb-react/shared/components/ShoppingCard/hooks/v2';
import { findCartItem } from 'hb-react/shared/components/ShoppingCard/utils';

export const transformCartItems = ({
  cart,
  event,
}: {
  cart: Pick<Cart, 'cart_items' | 'currency'>;
  event: Pick<Event, 'tickets' | 'addons' | 'donations' | 'standard_fields'>;
}): [TicketItem[], Attendee[], AddonItem[], DonationItem[]] => {
  const cartItems = cart?.cart_items || [];
  const eventTickets = event?.tickets || [];
  const eventAddons = event?.addons || [];
  const eventDonations = event?.donations || [];

  const cartTickets = eventTickets.map((ticket) => {
    const cartItems = (cart?.cart_items || []).filter(
      findCartItem<TicketCartItem>(ticket.id, 'NetworkEvents::Ticket'),
    );

    const [cartItem] = cartItems;

    return {
      ...ticket,
      cartItem: {
        ...cartItem,
        // Use a string "0" instead of a number 0 to match the options of the select component
        // in BuyableTicket. This is needed because a value of 0 is falsy and would make the select
        // component toggle between controlled and uncontrolled mode and complain.
        quantity: cartItems?.length || ('0' as unknown as number),
      },
    };
  });

  const attendees = cartItems
    .filter(
      (item): item is TicketCartItem =>
        'line_itemable_type' in item &&
        item.line_itemable_type === 'NetworkEvents::Ticket',
    )
    .map((item): Attendee => {
      const uuid = item?.metadata?.event?.uuid;

      const ticket = eventTickets.find(
        ({ id }) => id === item.line_itemable_id,
      );

      return {
        email: '',
        firstname: '',
        lastname: '',
        ...[
          ...Object.values(event?.standard_fields || {}),
          ...(ticket?.customizable_attributes || []),
        ].reduce(
          (fields, field) => ({
            [field.name]: '',
            ...fields,
          }),
          {},
        ),
        ...item,
        ...item.metadata?.event,

        mobile_phone: item.metadata?.event?.mobile_phone ?? '',
        ticket: eventTickets.find(({ id }) => id === item.line_itemable_id)!,

        addons: eventAddons
          .filter(
            ({ associated_tickets: associatedTickets, standalone }) =>
              !standalone && associatedTickets.includes(item.line_itemable_id),
          )
          .map((addon) => {
            const cartItem = cart?.cart_items.find(
              (item): item is AddonCartItem =>
                item.line_itemable_type === 'Ecommerce::Products::EventAddon' &&
                item.line_itemable_id === addon.id &&
                item.metadata?.event?.parent_uuid === uuid,
            );

            return {
              ...addon,
              ...addon?.customizable_attributes?.reduce(
                (fields, field) => ({
                  [field.name]: '',
                  ...fields,
                }),
                {},
              ),
              ...cartItem?.metadata?.event,
              selected: !!cartItem?.quantity,
              cartItem,
            };
          }),
      };
    });

  const addons = eventAddons
    .filter(({ standalone }) => standalone)
    .map((addon) => {
      const cartItem = cart?.cart_items?.find(
        findCartItem<AddonCartItem>(
          addon.id,
          'Ecommerce::Products::EventAddon',
        ),
      );

      return {
        ...addon,
        ...addon?.customizable_attributes.reduce(
          (fields, field) => ({
            [field.name]: '',
            ...fields,
          }),
          {},
        ),
        ...cartItem?.metadata?.event,
        cartItem,
        selected: cartItem?.quantity,
      };
    });

  const donations: DonationItem[] = eventDonations
    .filter(
      (donation) =>
        cart?.currency && donation.currency.code === cart.currency.code,
    )
    .map((donation) => {
      const cartItem = cart?.cart_items?.find(
        findCartItem<DonationCartItem>(donation.id, 'Donation::Campaign'),
      );

      return {
        ...donation,
        cartItem,
      };
    });

  return [cartTickets, attendees, addons, donations];
};
